// https://github.com/manniL/anchor-tags-to-nuxt-links
import type { Ref, ObjectDirective } from 'vue'
import { useRuntimeConfig, useNuxtApp } from '#imports'

type InterpolationElement = HTMLElement & {
    $componentUpdated?: () => void
    $destroy?: () => void
}

// Shape of the $i18n instance exposed by @nuxtjs/i18n on the Nuxt app
type NuxtI18n = {
    locale?: Ref<string>
    defaultLocale?: string
    availableLocales?: readonly string[]
}

export const vInterpolate: ObjectDirective<InterpolationElement> = {
    mounted(el) {
        // Only Nuxt-level composables (useNuxtApp, useRuntimeConfig, navigateTo) are
        // safe inside directive hooks. Vue's inject()-based composables (useLocalePath,
        // useInternationalization, useI18n) do NOT work here because Vue does not set
        // currentInstance when invoking directive lifecycle hooks.
        const nuxtApp = useNuxtApp()
        const baseURL = useRuntimeConfig().app.baseURL as string

        const links = Array.from(el.getElementsByTagName('a')).filter((linkEl) => {
            const href = linkEl.getAttribute('href')
            if (!href) return false
            return isInternalLink(href)
        })

        function handleClick(event: Event) {
            event.preventDefault()

            const target = event.currentTarget as HTMLAnchorElement
            const href = target.getAttribute('href') as string

            // Strip basePath prefix injected by localizeHtml during SSR
            const basePrefix = baseURL && baseURL !== '/' ? baseURL.replace(/\/$/, '') : ''
            let path = basePrefix && href.startsWith(basePrefix) ? href.slice(basePrefix.length) : href

            // Strip locale prefix if present in the href
            const i18n = nuxtApp.$i18n as NuxtI18n | undefined
            const availableLocales = i18n?.availableLocales ?? []
            const firstSegment = path.replace(/^\//, '').split('/')[0]
            if (firstSegment && (availableLocales as string[]).includes(firstSegment)) {
                path = path.slice(`/${firstSegment}`.length) || '/'
            }

            // Re-apply locale prefix for non-default locales (mirrors formatLink behaviour)
            const locale = i18n?.locale?.value
            const defaultLocale = i18n?.defaultLocale
            if (locale && locale !== defaultLocale) {
                path = `/${locale}${path}`
            }

            navigateTo(path)
        }

        links.forEach((link) => link.addEventListener('click', handleClick, false))

        // cleanup
        el.$componentUpdated = () => {
            links.forEach((link) => link.removeEventListener('click', handleClick, false))
            nextTick(() => links.forEach((link) => link.addEventListener('click', handleClick, false)))
        }

        el.$destroy = () => links.forEach((link) => link.removeEventListener('click', handleClick, false))
    },
    updated: (el) => el.$componentUpdated?.(),
    beforeUnmount: (el) => el.$destroy?.()
}

function isInternalLink (href?: string) {
    return href?.startsWith('/')
}
